// Desktop local capture wiring (FE-17 web half; docs/implementation/OPUS-DESKTOP-CAPTURE-PRODUCER.md).
// Inside the desktop shell only, one DesktopCaptureProducer is fed the server-verified call, capture
// and `desktop.capture` state on every change, plus the call engine's microphone and remote tracks.
// The producer decides nothing on its own: it records only while the server says the call is
// recording with everyone's consent and the capability is enabled. Tonight the server keeps
// `desktop.capture` disabled, so the producer stays denied; enabling it later needs no client change.
import type { Call, CallCapture, Capability } from '@bcio-voice/contracts';
import type {
  DesktopCaptureAuthEvents, DesktopCaptureBridge, DesktopCapturePolicyInput, DesktopCaptureState, DesktopCaptureTrack,
} from '@bcio-voice/call-client/core';
import type { DesktopAuthState, DesktopBridge } from '../desktop/bridge.ts';

export interface CaptureProducerLike {
  readonly state: DesktopCaptureState;
  onStateChange(listener: (state: DesktopCaptureState) => void): () => void;
  updatePolicy(input: DesktopCapturePolicyInput | null): void;
  setTracks(next: { local?: DesktopCaptureTrack | null; remote?: DesktopCaptureTrack | null }): void;
  start(callId: string): Promise<void>;
  stop(): Promise<void>;
  dispose(): Promise<void>;
}

export interface CaptureModule {
  createProducer(deps: { capture: DesktopCaptureBridge; auth: DesktopCaptureAuthEvents }): CaptureProducerLike;
  decide(input: unknown, callId: string): { action: 'record' | 'pause' | 'end' | 'stop' };
}

/** Loaded only inside the desktop shell, so web users never download the producer. */
export async function loadCaptureModule(): Promise<CaptureModule> {
  const core = await import('@bcio-voice/call-client/core');
  return { createProducer: deps => new core.DesktopCaptureProducer(deps), decide: core.decideDesktopCapture };
}

const CAPTURE_METHODS = ['status', 'recordConsent', 'begin', 'append', 'reportSources', 'pause', 'resume', 'finalize', 'abort', 'retry', 'pauseUploads', 'discard', 'onEvent'] as const;

/** The preload's capture bridge, when every method the producer may call is present. */
export function captureBridgeOf(desktop: DesktopBridge | null): DesktopCaptureBridge | null {
  const c = desktop?.capture as Partial<Record<(typeof CAPTURE_METHODS)[number], unknown>> | undefined;
  return c && typeof c === 'object' && CAPTURE_METHODS.every(m => typeof c[m] === 'function') ? (desktop!.capture as DesktopCaptureBridge) : null;
}

/** Desktop auth adapter for the producer: it only reads `status`. */
export function captureAuthOf(desktop: DesktopBridge): DesktopCaptureAuthEvents {
  return { onChange: listener => desktop.auth.onChange(state => listener(state as Parameters<typeof listener>[0])) };
}

/**
 * `auth_generation`: changes on every sign-in, sign-out, tenant switch and token-family change. The
 * web workspace is rebuilt per user and tenant, so each instance starts a new random prefix; within
 * it, any desktop auth state other than `signed_in`, and every return to `signed_in` (a new token
 * family), moves the counter. A plain token refresh while signed in does not.
 */
export class AuthGeneration {
  private counter = 0;
  private lastStatus: DesktopAuthState['status'] | null = null;
  constructor(private readonly prefix: string) {}

  get value(): string {
    return `${this.prefix}-${this.counter}`;
  }

  /** Returns true when the generation moved. */
  observe(state: Pick<DesktopAuthState, 'status'>): boolean {
    const before = this.lastStatus;
    this.lastStatus = state.status;
    if (state.status === 'signed_in' && before === 'signed_in') return false;
    if (before === null && state.status === 'signed_in') return false;
    this.counter += 1;
    return true;
  }
}

export function randomGenerationPrefix(): string {
  const bytes = new Uint8Array(8);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

export interface CaptureInputs {
  tenantId: string;
  userId: string;
  authGeneration: string;
  /** The call being followed (live, or just ended), or null. */
  call: Call | null;
  /** The server's CallCapture for that call, or null when it has none. */
  capture: CallCapture | null;
  /** `desktop.capture` for this person, or null when the server didn't report it. */
  capability: Capability | null;
  /** False while live updates are out of sync or access is being re-checked: verified state is gone. */
  verified: boolean;
}

/** The producer's policy input, built only from server state; null when there is none to give. */
export function buildPolicyInput(i: CaptureInputs): DesktopCapturePolicyInput | null {
  if (!i.verified || !i.call || !i.tenantId || !i.userId) return null;
  const c = i.capture;
  return {
    tenant_id: i.tenantId,
    user_id: i.userId,
    auth_generation: i.authGeneration,
    call_id: i.call.id,
    call_state: i.call.state as DesktopCapturePolicyInput['call_state'],
    recording_state: i.call.recording_state as DesktopCapturePolicyInput['recording_state'],
    capture: c ? { version: c.version, policy_verified: c.policy_verified, all_consented: c.all_consented, observed_state: c.observed_state as DesktopCapturePolicyInput['recording_state'], policy_version: c.policy_version } : null,
    desktop_capture: i.capability ? { status: i.capability.status, allowed: i.capability.allowed } : null,
  };
}

export interface TrackUpdate {
  callId: string | null;
  local: MediaStreamTrack | null;
  remote: MediaStreamTrack | null;
}

/**
 * Drives one producer from workspace state. `start` runs once each time the followed call enters
 * recording (per the server); `stop` runs when that call ends; `updatePolicy(null)` on any loss of
 * verified state stops capture without draining.
 */
export class DesktopCaptureWiring {
  private callId: string | null = null;
  private armed = true;
  private lastTracks: TrackUpdate | null = null;
  private disposed = false;

  constructor(private readonly mod: CaptureModule, readonly producer: CaptureProducerLike) {}

  update(inputs: CaptureInputs): void {
    if (this.disposed) return;
    // No followed call (for example after an ended call was let go): keep the last policy, so an
    // ending capture can finish draining. Only lost verification clears it.
    if (inputs.verified && !inputs.call) return;
    const input = buildPolicyInput(inputs);
    this.producer.updatePolicy(input);
    if (!input) {
      this.armed = true;
      return;
    }
    if (input.call_id !== this.callId) {
      this.callId = input.call_id;
      this.armed = true;
      if (this.lastTracks && this.lastTracks.callId === input.call_id) this.producer.setTracks({ local: this.lastTracks.local, remote: this.lastTracks.remote });
    }
    const action = this.mod.decide(input, input.call_id).action;
    if (action === 'record') {
      if (this.armed) {
        this.armed = false;
        void this.producer.start(input.call_id);
      }
    } else if (action === 'end') {
      this.armed = true;
      void this.producer.stop();
    } else if (action === 'stop') {
      this.armed = true;
    }
  }

  /** Engine tracks for a call (attach, replace, end). The producer only listens to them. */
  tracks(update: TrackUpdate): void {
    if (this.disposed) return;
    this.lastTracks = update;
    if (update.callId === null || update.callId === this.callId || this.callId === null) {
      this.producer.setTracks({ local: update.local, remote: update.remote });
    }
  }

  async dispose(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;
    this.producer.updatePolicy(null);
    await this.producer.dispose();
  }
}

/** What the Spool panel shows. */
export interface LocalCaptureView {
  status: 'loading' | 'ready' | 'error';
  producer: DesktopCaptureState | null;
}

/** Small external store for the panel (useSyncExternalStore). */
export class LocalCaptureStore {
  private view: LocalCaptureView = { status: 'loading', producer: null };
  private readonly listeners = new Set<() => void>();
  getSnapshot = (): LocalCaptureView => this.view;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  set(view: LocalCaptureView): void {
    this.view = view;
    for (const l of [...this.listeners]) l();
  }
}
